import React, { useState, useEffect, useRef } from 'react'; // Force deploy
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getPublicApiUrl, getChatApiUrl } from '../../config/api';
import './ChatWidget.css';

const ChatWidget = ({ showCookieBanner = false }) => {
    const chatZIndex = showCookieBanner ? 10050 : 10030;

    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [sessionId, setSessionId] = useState(() => {
        // Восстанавливаем sessionId из localStorage при загрузке
        return localStorage.getItem('chat_session_id');
    });
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [clientInfo, setClientInfo] = useState(() => {
        // Восстанавливаем данные клиента из localStorage
        const saved = localStorage.getItem('chat_client_info');
        return saved ? JSON.parse(saved) : { name: '', email: '', phone: '', description: '' };
    });
    const [showClientForm, setShowClientForm] = useState(() => {
        // Если есть сохраненная сессия, не показываем форму
        return !localStorage.getItem('chat_session_id');
    });
    const [isConnected, setIsConnected] = useState(true); // Чат доступен по умолчанию
    const [isSending, setIsSending] = useState(false);
    const [isAdminTyping, setIsAdminTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const autoMessageTimeoutRef = useRef(null);
    const lastClientMessageTimeRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Automatiškai nustatome isConnected pagal sessionId
    useEffect(() => {
        setIsConnected(!!sessionId);
    }, [sessionId]);

    // Восстанавливаем сессию при загрузке
    useEffect(() => {
        const restoreSession = async () => {
            const savedSessionId = localStorage.getItem('chat_session_id');
            const savedClientInfo = localStorage.getItem('chat_client_info');


            if (savedSessionId && savedClientInfo) {
                try {
                    // Восстанавливаем данные клиента
                    const clientData = JSON.parse(savedClientInfo);
                    setClientInfo(clientData);

                    // Проверяем, что сессия еще активна
                    const response = await fetch(`${getChatApiUrl('chat_get_messages.php')}?session_id=${savedSessionId}&last_message_id=0`);
                    const data = await response.json();

                    if (data.success) {
                        // Сессия активна, загружаем сообщения
                        setSessionId(savedSessionId);
                        setMessages(data.messages || []);
                        setShowClientForm(false);
                    } else {
                        // Сессия неактивна, очищаем localStorage
                        localStorage.removeItem('chat_session_id');
                        localStorage.removeItem('chat_client_info');
                        setSessionId(null);
                        setShowClientForm(true);
                        setClientInfo({ name: '', email: '', phone: '', description: '' });
                    }
                } catch (error) {
                    console.error('Error restoring session:', error);
                    // При ошибке очищаем localStorage
                    localStorage.removeItem('chat_session_id');
                    localStorage.removeItem('chat_client_info');
                    setSessionId(null);
                    setShowClientForm(true);
                    setClientInfo({ name: '', email: '', phone: '', description: '' });
                }
            } else {
                setShowClientForm(true);
            }
        };

        restoreSession();
    }, []);

    useEffect(() => {
        if (sessionId && isOpen) {
            // Polling для получения новых сообщений (каждые 5 секунд)
            pollIntervalRef.current = setInterval(() => {
                fetchMessages();
            }, 5000);

            // Планируем автоматические сообщения
            // Первое сообщение через 2 минуты
            scheduleAutoMessage(2 * 60 * 1000, 'follow_up');
            // Второе сообщение через 5 минут
            scheduleAutoMessage(5 * 60 * 1000, 'still_here');
            // Третье сообщение через 10 минут
            scheduleAutoMessage(10 * 60 * 1000, 'closing');

            return () => {
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                }
                if (autoMessageTimeoutRef.current) {
                    clearTimeout(autoMessageTimeoutRef.current);
                }
            };
        }
    }, [sessionId, isOpen]);

    const startChatSession = async () => {
        if (!clientInfo.name || !clientInfo.email) {
            alert(t('chat.validation.requiredFields'));
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(getChatApiUrl('chat_start_session.php'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...clientInfo,
                    language: i18n.language
                })
            });

            const data = await response.json();

            if (data.success) {
                setSessionId(data.session_id);
                setShowClientForm(false);
                // isConnected bus nustatytas automatiškai per useEffect
                // Не добавляем приветственное сообщение здесь, оно придет с сервера
                setMessages([]);

                // Сохраняем данные в localStorage
                localStorage.setItem('chat_session_id', data.session_id);
                localStorage.setItem('chat_client_info', JSON.stringify(clientInfo));
            } else {
                alert(data.message);
            }
        } catch (error) {
            console.error('Error starting chat session:', error);
            alert(t('chat.errors.connectionFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMessages = async () => {
        if (!sessionId) return;

        try {
            const response = await fetch(`${getChatApiUrl('chat_get_messages.php')}?session_id=${sessionId}&last_message_id=${messages.length > 0 ? messages[messages.length - 1].id : 0}`);
            const data = await response.json();

            if (data.success && data.messages.length > 0) {
                setMessages(prev => {
                    // Фильтруем дублирующиеся сообщения по ID
                    const existingIds = new Set(prev.map(msg => msg.id));
                    const newMessages = data.messages.filter(msg => !existingIds.has(msg.id));
                    return [...prev, ...newMessages];
                });
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !sessionId || isSending) return;

        const messageToSend = newMessage.trim();
        setNewMessage('');
        setIsSending(true);

        // Сразу добавляем сообщение в UI
        const tempMessage = {
            id: 'temp_' + Date.now(),
            message: messageToSend,
            sender_type: 'client',
            created_at: new Date().toISOString(),
            client_name: clientInfo.name,
            client_email: clientInfo.email,
            client_phone: clientInfo.phone
        };

        setMessages(prev => [...prev, tempMessage]);

        // Обновляем время последнего сообщения от клиента
        lastClientMessageTimeRef.current = Date.now();

        // Сбрасываем таймеры автоматических сообщений при активности пользователя
        if (autoMessageTimeoutRef.current) {
            clearTimeout(autoMessageTimeoutRef.current);
        }

        try {
            const response = await fetch(getChatApiUrl('chat_send_message.php'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    message: messageToSend,
                    sender_type: 'client'
                })
            });

            const data = await response.json();

            if (data.success) {
                // Удаляем временное сообщение и получаем реальное через fetchMessages
                setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
                // fetchMessages будет вызван автоматически через useEffect
            } else {
                alert(data.message);
                setNewMessage(messageToSend); // Восстанавливаем сообщение при ошибке
                // Удаляем временное сообщение при ошибке
                setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert(t('chat.errors.sendFailed'));
            setNewMessage(messageToSend);
            // Удаляем временное сообщение при ошибке
            setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Функция для отправки автоматических сообщений
    const sendAutoMessage = async (messageType) => {
        if (!sessionId) return;

        const autoMessages = {
            follow_up: t('chat.auto_messages.follow_up'),
            still_here: t('chat.auto_messages.still_here'),
            closing: t('chat.auto_messages.closing')
        };

        const message = autoMessages[messageType];
        if (!message) return;

        // Показываем индикатор печати перед отправкой
        simulateAdminTyping();

        try {
            const response = await fetch(getChatApiUrl('chat_send_message.php'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    message: message,
                    sender_type: 'admin'
                })
            });

            const data = await response.json();
            if (data.success) {
            }
        } catch (error) {
            console.error('Error sending auto message:', error);
        }
    };

    // Функция для планирования автоматических сообщений
    const scheduleAutoMessage = (delay, messageType) => {
        if (autoMessageTimeoutRef.current) {
            clearTimeout(autoMessageTimeoutRef.current);
        }

        autoMessageTimeoutRef.current = setTimeout(() => {
            sendAutoMessage(messageType);
        }, delay);
    };

    // Функция для имитации печати администратора
    const simulateAdminTyping = () => {
        setIsAdminTyping(true);

        // Очищаем предыдущий таймер
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        // Скрываем индикатор через 3 секунды
        typingTimeoutRef.current = setTimeout(() => {
            setIsAdminTyping(false);
        }, 3000);
    };

    const toggleChat = () => {
        if (!isOpen) {
            setIsOpen(true);
            setIsMinimized(false);
            // Убеждаемся, что форма показывается если нет сессии
            if (!sessionId) {
                setShowClientForm(true);
            }
        } else {
            const newMinimizedState = !isMinimized;
            setIsMinimized(newMinimizedState);
        }
    };

    const toggleMinimize = () => {
        const newMinimizedState = !isMinimized;
        setIsMinimized(newMinimizedState);
    };

    const closeChat = () => {
        setIsOpen(false);
        setIsMinimized(false);

        // Очищаем таймеры автоматических сообщений
        if (autoMessageTimeoutRef.current) {
            clearTimeout(autoMessageTimeoutRef.current);
            autoMessageTimeoutRef.current = null;
        }
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }
    };

    const resetChat = () => {
        setIsOpen(false);
        setIsMinimized(false);
        setSessionId(null);
        setMessages([]);
        setShowClientForm(true);
        setIsConnected(false);
        setIsSending(false);
        setIsAdminTyping(false);
        setNewMessage('');
        setClientInfo({ name: '', email: '', phone: '', description: '' });

        // Очищаем localStorage
        localStorage.removeItem('chat_session_id');
        localStorage.removeItem('chat_client_info');

        // Очищаем таймеры автоматических сообщений
        if (autoMessageTimeoutRef.current) {
            clearTimeout(autoMessageTimeoutRef.current);
            autoMessageTimeoutRef.current = null;
        }
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }
    };

    // Дополнительная проверка для предотвращения проблем с состоянием
    useEffect(() => {
        if (isOpen && !sessionId && !showClientForm) {
            setShowClientForm(true);
        }
    }, [isOpen, sessionId, showClientForm]);

    // Отладка позиционирования кнопки чата
    useEffect(() => {
        const checkButtonPosition = () => {
            const button = document.querySelector('.chat-widget-toggle');
            if (button) {
                const computedStyle = window.getComputedStyle(button);
                const rect = button.getBoundingClientRect();
                console.log('🔍 Кнопка чата найдена:');
                console.log('  - position:', computedStyle.position);
                console.log('  - bottom:', computedStyle.bottom);
                console.log('  - right:', computedStyle.right);
                console.log('  - z-index:', computedStyle.zIndex);
                console.log('  - display:', computedStyle.display);
                console.log('  - getBoundingClientRect():', {
                    top: rect.top,
                    right: rect.right,
                    bottom: rect.bottom,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height
                });
                console.log('  - parentElement:', button.parentElement?.tagName);
                console.log('  - parentElement className:', button.parentElement?.className);
            } else {
                console.log('❌ Кнопка чата не найдена в DOM');
            }
        };

        // Проверяем сразу
        checkButtonPosition();

        // Проверяем через небольшую задержку
        const timer = setTimeout(checkButtonPosition, 100);
        const timer2 = setTimeout(checkButtonPosition, 500);
        const timer3 = setTimeout(checkButtonPosition, 1000);

        return () => {
            clearTimeout(timer);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [isOpen]);

    if (!isOpen) {
        // Проверяем, что document.body существует
        if (!document.body) {
            console.log('❌ document.body не существует');
            return null;
        }

        console.log('🔍 Рендерим кнопку чата с position: fixed');
        console.log('🔍 Window width:', window.innerWidth);
        console.log('🔍 Cookie banner visible:', document.body.classList.contains('cookie-banner-visible'));
        console.log('🔍 document.body:', document.body);
        console.log('🔍 document.body children count:', document.body.children.length);

        return createPortal(
            <div
                className="chat-widget-toggle"
                onClick={toggleChat}
            >
                <div className="chat-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" fill="currentColor" />
                        <path d="M7 9H17V11H7V9ZM7 12H15V14H7V12Z" fill="currentColor" />
                    </svg>
                </div>
                <span className="chat-toggle-text">{t('chat.toggleText')}</span>
            </div>,
            document.body
        );
    }

    // Проверяем, что document.body существует
    if (!document.body) {
        return null;
    }

    return createPortal(
        <div
            className={`chat-widget ${isMinimized ? 'minimized' : ''}`}
        >
            <div
                className="chat-header"
                onClick={isMinimized ? toggleMinimize : undefined}
            >

                {/* LEFT SIDE - Status and info */}
                <div className="chat-header-left">
                    {/* Status indicator */}
                    <div
                        className="status-indicator"
                    >
                        <div className={`status-dot ${isConnected ? 'online' : 'offline'}`}></div>
                        <span>{isConnected ? t('chat.status.connected') : t('chat.status.disconnected')}</span>
                    </div>


                    {/* User info */}
                    {!isMinimized && sessionId && (
                        <div className="chat-user-info">
                            <span>{clientInfo.name}</span>
                        </div>
                    )}
                </div>

                {/* RIGHT SIDE - Control buttons */}
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="chat-header-right"
                >
                    {/* New chat button */}
                    {(sessionId || isMinimized) && (
                        <button
                            onClick={() => {
                                if (window.confirm(t('chat.confirm.new_chat'))) {
                                    resetChat();
                                }
                            }}
                            title={t('chat.typing.new_chat')}
                            className="new-chat-btn"
                        >
                            💬
                        </button>
                    )}
                    <button
                        onClick={() => {
                            toggleMinimize();
                        }}
                        title={isMinimized ? t('chat.controls.expand') : t('chat.controls.minimize')}
                        className="minimize-btn"
                    >
                        {isMinimized ? '▲' : '▼'}
                    </button>
                    <button
                        onClick={() => {
                            if (sessionId) {
                                // Если есть активная сессия, спрашиваем подтверждение
                                if (window.confirm(t('chat.confirm.close'))) {
                                    resetChat();
                                }
                            } else {
                                // Если нет сессии, просто закрываем
                                closeChat();
                            }
                        }}
                        title={t('chat.controls.close')}
                        className="close-btn"
                    >
                        ×
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <div className="chat-content">
                    {showClientForm ? (
                        <div className="client-form">
                            <h3>{t('chat.form.title')}</h3>
                            <p>{t('chat.form.description')}</p>
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder={t('chat.form.name')}
                                    value={clientInfo.name}
                                    onChange={(e) => setClientInfo({ ...clientInfo, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <input
                                    type="email"
                                    placeholder={t('chat.form.email')}
                                    value={clientInfo.email}
                                    onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <input
                                    type="tel"
                                    placeholder={t('chat.form.phone')}
                                    value={clientInfo.phone}
                                    onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <textarea
                                    placeholder={t('chat.form.description')}
                                    value={clientInfo.description}
                                    onChange={(e) => setClientInfo({ ...clientInfo, description: e.target.value })}
                                    rows="2"
                                />
                            </div>
                            <button
                                className="start-chat-btn"
                                onClick={startChatSession}
                                disabled={isLoading}
                            >
                                {isLoading ? t('chat.form.starting') : t('chat.form.start')}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="messages-container">
                                {messages.map((message, index) => (
                                    <div
                                        key={`${message.id}-${index}`}
                                        className={`message ${message.sender_type === 'client' ? 'client' : 'admin'}`}
                                    >
                                        {message.sender_type === 'client' && (message.client_name || message.client_email) && (
                                            <div className="message-sender-info">
                                                <span className="sender-name">{message.client_name}</span>
                                                {message.client_email && (
                                                    <span className="sender-email">({message.client_email})</span>
                                                )}
                                                {message.client_phone && (
                                                    <span className="sender-phone"> - {message.client_phone}</span>
                                                )}
                                            </div>
                                        )}
                                        <div className="message-content">
                                            {message.message}
                                        </div>
                                        <div className="message-time">
                                            {new Date(message.created_at).toLocaleTimeString()}
                                        </div>
                                    </div>
                                ))}

                                {/* Индикатор печати администратора */}
                                {isAdminTyping && (
                                    <div className="message admin typing-indicator">
                                        <div className="message-content">
                                            <div className="typing-dots">
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                            <span className="typing-text">{t('chat.typing.admin_typing')}</span>
                                        </div>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </div>

                            <div className="message-input-container">
                                <textarea
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder={isSending ? t('chat.typing.sending') : t('chat.input.placeholder')}
                                    rows="2"
                                    disabled={!sessionId || isSending}
                                />
                                <button
                                    className="send-btn"
                                    onClick={sendMessage}
                                    disabled={!newMessage.trim() || !sessionId || isSending}
                                >
                                    {isSending ? (
                                        <div className="sending-spinner">
                                            <div className="typing-dots">
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                            <span className="sending-text">{t('chat.typing.sending')}</span>
                                        </div>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>,
        document.body
    );
};

export default ChatWidget;